import { useState } from "react"
import { router } from "@inertiajs/react"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"
import { Sun, Moon, Monitor, Check } from "lucide-react"
import { COLOR_OPTIONS, type ColorOption } from "@/lib/colorPalette"
import { FONT_OPTIONS, FONT_PAIRINGS, getFontOption } from "@/lib/fontPalette"
import { applyAppearance } from "@/lib/theme"
import type { UserSettings } from "./SettingsPage"

type ThemeMode = "light" | "dark" | "system"

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

async function updateSetting(key: string, value: string): Promise<void> {
  await fetch("/api/user_settings", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCSRFToken(),
    },
    body: JSON.stringify({ [key]: value }),
  })
}

async function updateSettings(data: Record<string, string>): Promise<void> {
  await fetch("/api/user_settings", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCSRFToken(),
    },
    body: JSON.stringify(data),
  })
}

const THEME_MODE_OPTIONS: Array<{
  value: ThemeMode
  label: string
  icon: React.ElementType
}> = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
]

interface AppearanceTabProps {
  settings: UserSettings
}

export function AppearanceTab({ settings }: AppearanceTabProps) {
  const [themeMode, setThemeMode] = useState<ThemeMode>(settings.theme_mode)
  const [primaryColor, setPrimaryColor] = useState<string>(settings.primary_color)
  const [secondaryColor, setSecondaryColor] = useState<string>(settings.secondary_color)
  const [headingFont, setHeadingFont] = useState<string>(settings.heading_font || "inter")
  const [bodyFont, setBodyFont] = useState<string>(settings.body_font || "inter")

  function applyCurrentAppearance(overrides: Partial<{
    theme_mode: ThemeMode
    primary_color: string
    secondary_color: string
    heading_font: string
    body_font: string
  }> = {}) {
    applyAppearance({
      theme_mode: overrides.theme_mode ?? themeMode,
      primary_color: overrides.primary_color ?? primaryColor,
      secondary_color: overrides.secondary_color ?? secondaryColor,
      heading_font: overrides.heading_font ?? headingFont,
      body_font: overrides.body_font ?? bodyFont,
    })
  }

  async function handleThemeModeChange(mode: ThemeMode) {
    setThemeMode(mode)
    applyCurrentAppearance({ theme_mode: mode })
    await updateSetting("theme_mode", mode)
    router.reload({ only: ["appearance"] })
  }

  async function handleColorChange(
    key: "primary_color" | "secondary_color",
    value: string,
  ) {
    if (key === "primary_color") setPrimaryColor(value)
    else setSecondaryColor(value)
    applyCurrentAppearance({ [key]: value })
    await updateSetting(key, value)
    router.reload({ only: ["appearance"] })
  }

  async function handleFontChange(
    key: "heading_font" | "body_font",
    value: string,
  ) {
    if (key === "heading_font") setHeadingFont(value)
    else setBodyFont(value)
    applyCurrentAppearance({ [key]: value })
    await updateSetting(key, value)
    router.reload({ only: ["appearance"] })
  }

  async function handlePairingSelect(headingName: string, bodyName: string) {
    setHeadingFont(headingName)
    setBodyFont(bodyName)
    applyCurrentAppearance({ heading_font: headingName, body_font: bodyName })
    await updateSettings({ heading_font: headingName, body_font: bodyName })
    router.reload({ only: ["appearance"] })
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold">Theme Mode</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose how the app should look. "System" follows your device setting.
        </p>
        <Separator className="my-4" />

        <div className="grid grid-cols-3 gap-3">
          {THEME_MODE_OPTIONS.map(({ value, label, icon: Icon }) => {
            const selected = themeMode === value
            return (
              <button
                key={value}
                type="button"
                onClick={() => handleThemeModeChange(value)}
                className={cn(
                  "flex flex-col items-center gap-2 rounded-lg border p-4 text-sm font-medium transition-colors",
                  selected
                    ? "border-primary bg-primary/5 text-foreground"
                    : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                )}
                aria-pressed={selected}
              >
                <Icon className="h-5 w-5" />
                <span>{label}</span>
              </button>
            )
          })}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold">Theme Colors</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          The primary color is used for buttons, links and other primary actions.
          The secondary color is used for flash messages and status highlights.
        </p>
        <Separator className="my-4" />

        <div className="space-y-6">
          <ColorSelector
            label="Primary Color"
            value={primaryColor}
            onChange={(name) => handleColorChange("primary_color", name)}
          />

          <ColorSelector
            label="Secondary Color"
            value={secondaryColor}
            onChange={(name) => handleColorChange("secondary_color", name)}
          />
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold">Note Fonts</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose fonts for your notes. Pick a pre-made pairing or select individual fonts for headings and body text.
        </p>
        <Separator className="my-4" />

        <div className="space-y-6">
          {/* Font pairings */}
          <div>
            <Label>Font Pairings</Label>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {FONT_PAIRINGS.map((pairing) => {
                const selected = headingFont === pairing.heading && bodyFont === pairing.body
                const headingFontObj = getFontOption(pairing.heading)
                const bodyFontObj = getFontOption(pairing.body)
                return (
                  <button
                    key={pairing.name}
                    type="button"
                    onClick={() => handlePairingSelect(pairing.heading, pairing.body)}
                    className={cn(
                      "flex flex-col rounded-lg border p-3 text-left transition-all",
                      selected
                        ? "border-foreground ring-2 ring-foreground/10"
                        : "border-border hover:border-foreground/40",
                    )}
                    aria-pressed={selected}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium">{pairing.label}</span>
                      {selected && <Check className="h-3.5 w-3.5" />}
                    </div>
                    <span
                      className="text-base font-semibold leading-tight"
                      style={{ fontFamily: headingFontObj.family }}
                    >
                      Heading Text
                    </span>
                    <span
                      className="text-sm text-muted-foreground mt-0.5"
                      style={{ fontFamily: bodyFontObj.family }}
                    >
                      Body text preview
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <Separator />

          {/* Individual font selectors */}
          <div>
            <Label>Heading Font</Label>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {FONT_OPTIONS.map((font) => {
                const selected = headingFont === font.name
                return (
                  <button
                    key={font.name}
                    type="button"
                    onClick={() => handleFontChange("heading_font", font.name)}
                    className={cn(
                      "flex flex-col rounded-lg border p-3 text-left transition-all",
                      selected
                        ? "border-foreground ring-2 ring-foreground/10"
                        : "border-border hover:border-foreground/40",
                    )}
                    aria-pressed={selected}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-medium text-muted-foreground">{font.label}</span>
                      {selected && <Check className="h-3 w-3" />}
                    </div>
                    <span
                      className="text-sm font-semibold"
                      style={{ fontFamily: font.family }}
                    >
                      Aa
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <Label>Body Font</Label>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {FONT_OPTIONS.map((font) => {
                const selected = bodyFont === font.name
                return (
                  <button
                    key={font.name}
                    type="button"
                    onClick={() => handleFontChange("body_font", font.name)}
                    className={cn(
                      "flex flex-col rounded-lg border p-3 text-left transition-all",
                      selected
                        ? "border-foreground ring-2 ring-foreground/10"
                        : "border-border hover:border-foreground/40",
                    )}
                    aria-pressed={selected}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-medium text-muted-foreground">{font.label}</span>
                      {selected && <Check className="h-3 w-3" />}
                    </div>
                    <span
                      className="text-sm"
                      style={{ fontFamily: font.family }}
                    >
                      Aa
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

interface ColorSelectorProps {
  label: string
  value: string
  onChange: (name: string) => void
}

function ColorSelector({ label, value, onChange }: ColorSelectorProps) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {COLOR_OPTIONS.map((color) => (
          <ColorOptionButton
            key={color.name}
            color={color}
            selected={value === color.name}
            onSelect={() => onChange(color.name)}
          />
        ))}
      </div>
    </div>
  )
}

interface ColorOptionButtonProps {
  color: ColorOption
  selected: boolean
  onSelect: () => void
}

function ColorOptionButton({ color, selected, onSelect }: ColorOptionButtonProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group flex flex-col gap-2 rounded-lg border p-3 text-left transition-all",
        selected
          ? "border-foreground ring-2 ring-foreground/10"
          : "border-border hover:border-foreground/40",
      )}
      aria-pressed={selected}
      title={color.label}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium">{color.label}</span>
        {selected && <Check className="h-3.5 w-3.5" />}
      </div>
      <div className="flex gap-1.5">
        <ColorPreview shade="Light" bg={color.light.bg} fg={color.light.fg} />
        <ColorPreview shade="Dark" bg={color.dark.bg} fg={color.dark.fg} />
      </div>
    </button>
  )
}

function ColorPreview({ shade, bg, fg }: { shade: string; bg: string; fg: string }) {
  return (
    <div
      className="flex h-9 flex-1 items-center justify-center rounded-md text-[10px] font-semibold uppercase tracking-wide"
      style={{ backgroundColor: bg, color: fg }}
    >
      {shade}
    </div>
  )
}
